import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CgProfile } from "react-icons/cg";
import { useAuth } from '../../context/AuthContext';

import brandLogo from '../../assets/img/brand-logo.svg';
import Login from '../shorts/Login';

import '../../assets/css/header.css';

function canAccessDashboard(user, roleIdFromContext) {
    if (!user) return false;

    // numerische Rollen-ID bevorzugt
    const numericRole = Number(roleIdFromContext ?? user.role_id ?? user.role ?? 0);
    if ([1, 2, 3].includes(numericRole)) return true;

    // Fallback: Rollennamen
    const name = String(user.role_name || user.roleName || '').toLowerCase();
    return ['admin', 'redakteur', 'moderator'].includes(name);
}

const Header = () => {
    const { user, logout, roleId } = useAuth();
    const [showLogin, setShowLogin] = useState(false);
    const [showLogout, setShowLogout] = useState(false);
    const [navOpen, setNavOpen] = useState(false);

    const showDashboard = canAccessDashboard(user, roleId);

    // ESC schließt alles
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setShowLogin(false);
                setShowLogout(false);
                setNavOpen(false);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    const handleLogout = () => {
        logout();
        setShowLogout(false);
    };

    // Body-Scroll sperren, wenn ein Overlay offen ist
    useEffect(() => {
        const anyOverlayOpen = showLogin || showLogout || navOpen;
        document.body.style.overflow = anyOverlayOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [showLogin, showLogout, navOpen]);

    const handleNavItemClick = () => setNavOpen(false);

    return (
        <header>
            <div className="wrapper">
                <a href="/" className="brand-link" aria-label="Startseite">
                    <img className="brand-logo" src={brandLogo} alt="NGCTV Logo" title="NGCTV Logo"/>
                </a>

                {/* Hamburger */}
                <button
                    className={`nav-toggle ${navOpen ? 'is-open' : ''}`}
                    aria-label="Navigation umschalten"
                    aria-expanded={navOpen}
                    aria-controls="primary-navigation"
                    onClick={() => setNavOpen(!navOpen)}
                >
                    <span className="bar" />
                    <span className="bar" />
                    <span className="bar" />
                </button>

                {/* Navigation */}
                <nav
                    id="primary-navigation"
                    className={`primary-nav ${navOpen ? 'open' : ''}`}
                    aria-hidden={!navOpen}
                >
                    {/* Schließen-Button nur Mobile sichtbar */}
                    <button
                        className="close-nav-btn"
                        onClick={() => setNavOpen(false)}
                        aria-label="Navigation schließen"
                    >
                        ✖
                    </button>

                    <ul>
                        <li><Link to="/" viewTransition onClick={handleNavItemClick}>Startseite</Link></li>
                        <li><Link to="/news" viewTransition onClick={handleNavItemClick}>News</Link></li>
                        <li><Link to="/tests" viewTransition onClick={handleNavItemClick}>Tests & Reviews</Link></li>
                        <li><Link to="/community" viewTransition onClick={handleNavItemClick}>Community</Link></li>
                    </ul>
                </nav>

                {/* Login/Profile */}
                <div className="login-link-box">
                    {user ? (
                        <button className="login-link as-button" onClick={() => setShowLogout(true)}>
                            <CgProfile className="login-icon" />
                            <span className="login-text">{user.username}</span>
                        </button>
                    ) : (
                        <button className="login-link as-button" onClick={() => setShowLogin(true)}>
                            <CgProfile className="login-icon" />
                            <span className="login-text">Anmelden</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Login-Sidebar */}
            {showLogin && (
                <>
                    <div className="login-sidebar slide-in">
                        <button className="close-btn" onClick={() => setShowLogin(false)}>✖</button>
                        <Login />
                    </div>
                    <div className="overlay fade-in" onClick={() => setShowLogin(false)} />
                </>
            )}

            {/* Logout-Sidebar */}
            {showLogout && user && (
                <>
                    <div className="logout-sidebar slide-in">
                        <button className="close-btn" onClick={() => setShowLogout(false)}>✖</button>
                        <h3>Willkommen, {user.username}!</h3>

                        {/* Schnellzugriff aufs Dashboard */}
                        {showDashboard && (
                            <Link
                                to="/dashboard"
                                className="nav-link"
                                onClick={() => { setShowLogout(false); setNavOpen(false); }}
                                style={{ marginBottom: 12 }}
                            >
                                Zum Dashboard
                            </Link>
                        )}

                        <button className="btn logout-btn" onClick={handleLogout}>Logout</button>
                    </div>
                    <div className="overlay fade-in" onClick={() => setShowLogout(false)} />
                </>
            )}

            {/* Overlay fürs Mobile-Menü */}
            {navOpen && <div className="overlay fade-in" onClick={() => setNavOpen(false)} />}
        </header>
    );
};

export default Header;
